/**
 * Enhanced Geolocation Utility untuk Cross-Platform Compatibility
 * Khusus untuk iOS/iPhone dan semua perangkat
 */

// Geolocation options untuk iOS compatibility
export const geolocationOptions = {
  enableHighAccuracy: true,
  timeout: 15000, // Increased timeout for iOS
  maximumAge: 60000, // 1 minute cache
};

// Opsi cadangan bila high-accuracy gagal/timeout
export const geolocationOptionsFallback = {
  enableHighAccuracy: false,
  timeout: 20000,
  maximumAge: 120000,
};

// Error messages untuk iOS/iPhone
export const geolocationErrorMessages = {
  PERMISSION_DENIED:
    "Akses lokasi ditolak. Izinkan lokasi untuk situs ini di pengaturan browser. Untuk iOS: Settings > Privacy & Security > Location Services > Safari/Chrome > Allow While Using App.",
  POSITION_UNAVAILABLE:
    "Informasi lokasi tidak tersedia. Pastikan GPS aktif dan koneksi internet stabil.",
  TIMEOUT:
    "Timeout dalam mendapatkan lokasi. Coba refresh halaman atau pindah ke area dengan sinyal GPS yang lebih baik.",
  NOT_SUPPORTED:
    "Geolocation tidak didukung oleh browser ini. Gunakan browser modern seperti Safari, Chrome, atau Firefox.",
  INSECURE_CONTEXT:
    "Akses lokasi hanya bisa di HTTPS atau localhost. Buka situs lewat https://smartdh.rotendaokab.go.id",
  POLICY_BLOCKED:
    "Akses lokasi diblokir oleh kebijakan server (Permissions-Policy). Hubungi admin untuk mengizinkan geolocation=(self).",
  DEFAULT: "Terjadi kesalahan dalam mendapatkan lokasi. Coba refresh halaman.",
};

const requestPosition = (options) =>
  new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;

        if (
          latitude === null ||
          longitude === null ||
          isNaN(latitude) ||
          isNaN(longitude)
        ) {
          reject(new Error("Koordinat tidak valid"));
          return;
        }

        resolve({
          latitude,
          longitude,
          accuracy,
          timestamp: position.timestamp,
        });
      },
      (error) => {
        let errorMessage = geolocationErrorMessages.DEFAULT;

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = geolocationErrorMessages.PERMISSION_DENIED;
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = geolocationErrorMessages.POSITION_UNAVAILABLE;
            break;
          case error.TIMEOUT:
            errorMessage = geolocationErrorMessages.TIMEOUT;
            break;
          default:
            errorMessage = `${geolocationErrorMessages.DEFAULT}: ${error.message}`;
        }

        const enrichedError = new Error(errorMessage);
        enrichedError.code = error.code;
        reject(enrichedError);
      },
      options
    );
  });

// Enhanced getCurrentPosition untuk iOS compatibility
export const getCurrentPositionEnhanced = async (
  options = geolocationOptions
) => {
  if (!window.isSecureContext) {
    throw new Error(geolocationErrorMessages.INSECURE_CONTEXT);
  }

  if (!navigator.geolocation) {
    throw new Error(geolocationErrorMessages.NOT_SUPPORTED);
  }

  // Deteksi Permissions-Policy yang memblokir geolocation di production
  try {
    if (document.featurePolicy?.allowsFeature) {
      if (!document.featurePolicy.allowsFeature("geolocation")) {
        throw new Error(geolocationErrorMessages.POLICY_BLOCKED);
      }
    } else if (document.permissionsPolicy?.allowsFeature) {
      if (!document.permissionsPolicy.allowsFeature("geolocation")) {
        throw new Error(geolocationErrorMessages.POLICY_BLOCKED);
      }
    }
  } catch (policyError) {
    if (policyError.message === geolocationErrorMessages.POLICY_BLOCKED) {
      throw policyError;
    }
  }

  const primaryOptions = { ...options };
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari =
    /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);

  if (isIOS && isSafari) {
    primaryOptions.timeout = Math.max(primaryOptions.timeout || 0, 20000);
  }

  try {
    return await requestPosition(primaryOptions);
  } catch (error) {
    // Retry sekali tanpa high accuracy bila timeout / unavailable
    if (
      error.code === 2 ||
      error.code === 3 ||
      error.message === geolocationErrorMessages.TIMEOUT ||
      error.message === geolocationErrorMessages.POSITION_UNAVAILABLE
    ) {
      return requestPosition(geolocationOptionsFallback);
    }
    throw error;
  }
};

// Fallback coordinates untuk iOS/iPhone
export const getFallbackCoordinates = () => {
  return {
    latitude: -10.760178,
    longitude: 123.060573,
    accuracy: 0,
    isFallback: true,
  };
};

// Validate coordinates
export const validateCoordinates = (latitude, longitude) => {
  if (latitude === null || longitude === null) return false;
  if (isNaN(latitude) || isNaN(longitude)) return false;
  if (latitude < -90 || latitude > 90) return false;
  if (longitude < -180 || longitude > 180) return false;
  return true;
};

// Calculate distance between two points (Haversine formula)
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  return distance;
};

// Convert degrees to radians
const toRadians = (degrees) => {
  return degrees * (Math.PI / 180);
};

// Enhanced time utility untuk iOS compatibility
export const getCurrentTimeEnhanced = () => {
  // Gunakan waktu lokal perangkat langsung (jangan ubah dengan timezoneOffset —
  // itu justru merusak jam di iOS/Safari)
  const now = new Date();

  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");

  return {
    year,
    month,
    day,
    hours,
    minutes,
    seconds,
    fullDate: `${year}-${month}-${day}`,
    timeString: `${hours}:${minutes}`,
    timeStringWithSeconds: `${hours}:${minutes}:${seconds}`,
    dateTime: now,
    timezoneOffset: now.getTimezoneOffset(),
  };
};

// Check if device is iOS
export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

// Check if browser is Safari
export const isSafari = () => {
  return (
    /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent)
  );
};

// Get device info for debugging
export const getDeviceInfo = () => {
  let geolocationAllowedByPolicy = null;
  try {
    if (document.featurePolicy?.allowsFeature) {
      geolocationAllowedByPolicy =
        document.featurePolicy.allowsFeature("geolocation");
    } else if (document.permissionsPolicy?.allowsFeature) {
      geolocationAllowedByPolicy =
        document.permissionsPolicy.allowsFeature("geolocation");
    }
  } catch (e) {
    geolocationAllowedByPolicy = null;
  }

  return {
    userAgent: navigator.userAgent,
    isIOS: isIOS(),
    isSafari: isSafari(),
    geolocationSupported: !!navigator.geolocation,
    isSecureContext: !!window.isSecureContext,
    geolocationAllowedByPolicy,
    platform: navigator.platform,
    language: navigator.language,
  };
};
